// src/components/AccessSelector.tsx
import React, { useState, useEffect } from 'react';
import { Building2, CheckCircle2, Globe, Users, PlusCircle, X, Mail, Building, Lock, Users2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { getAllGroups, Group } from "@/lib/groups";

// Define access control types
export type AccessType = 'public' | 'private' | 'domain' | 'specific' | 'domains' | 'group';
export type AccessConfig = {
  type: AccessType;
  emails?: string[]; // Used only when type is 'specific'
  domains?: string[]; // Used only when type is 'domains'
  groups?: string[]; // Used only when type is 'group'
};

interface AccessSelectorProps {
  value: AccessType;
  emails?: string[];
  domains?: string[];
  groups?: string[];
  userDomain?: string;
  onChange: (config: AccessConfig) => void;
}

export function AccessSelector({ 
  value = 'private', 
  emails = [], 
  domains = [],
  groups = [],
  userDomain = '',
  onChange 
}: AccessSelectorProps) {
  // Input state for new items
  const [emailInput, setEmailInput] = useState('');
  const [domainInput, setDomainInput] = useState('');
  
  // Get available groups once on mount
  const availableGroups = getAllGroups();
  
  // IMPORTANT: Instead of maintaining separate state, we use the props directly
  // This avoids the infinite loop caused by state updates triggering re-renders

  // Handle access type change
  const handleAccessTypeChange = (type: AccessType) => {
    if (type === value) return; // No change

    // Prepare the config object dynamically
    let configToSend: AccessConfig = { type }; // Start with the type

    if (type === 'specific') {
      configToSend.emails = emails;
    } else if (type === 'domains') {
      configToSend.domains = domains;
    } else if (type === 'group') {
      configToSend.groups = groups;
    }
    // No special handling needed for type: 'domain', 'public', or 'private' here
    // as they don't require extra data in the AccessConfig based on the new logic.

    // Update parent component
    onChange(configToSend);
  };
  
  // Handle adding a specific email
  const handleAddEmail = () => {
    if (!emailInput.trim() || !validateEmail(emailInput)) return;
    
    const newEmail = emailInput.trim();
    
    // Don't add if already in the list
    if (emails.includes(newEmail)) {
      setEmailInput('');
      return;
    }
    
    const updatedEmails = [...emails, newEmail];
    setEmailInput('');
    
    // Update parent component
    onChange({
      type: 'specific',
      emails: updatedEmails
    });
  };

  // Handle adding a domain
  const handleAddDomain = () => {
    if (!domainInput.trim() || !validateDomain(domainInput)) return;
    
    const newDomain = domainInput.trim().toLowerCase();
    
    // Don't add if already in the list
    if (domains.includes(newDomain)) {
      setDomainInput('');
      return;
    }
    
    const updatedDomains = [...domains, newDomain];
    setDomainInput('');
    
    // Update parent component
    onChange({
      type: 'domains',
      domains: updatedDomains
    });
  };

  // Handle toggling a group selection
  const handleToggleGroup = (groupId: string) => {
    // Check if group is already selected
    const isSelected = groups.includes(groupId);
    
    let updatedGroups: string[];
    
    if (isSelected) {
      // Remove the group
      updatedGroups = groups.filter(g => g !== groupId);
    } else {
      // Add the group
      updatedGroups = [...groups, groupId];
    }
    
    // Update parent component
    onChange({
      type: 'group',
      groups: updatedGroups
    });
  };
  
  // Handle removing a specific email
  const handleRemoveEmail = (email: string) => {
    const updatedEmails = emails.filter(e => e !== email);
    
    // Update parent component
    onChange({
      type: 'specific',
      emails: updatedEmails
    });
  };

  // Handle removing a domain
  const handleRemoveDomain = (domain: string) => {
    const updatedDomains = domains.filter(d => d !== domain);
    
    // Update parent component
    onChange({
      type: 'domains',
      domains: updatedDomains
    });
  };
  
  // Email validation
  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Domain validation
  const validateDomain = (domain: string) => {
    return /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$/.test(domain);
  };

  // Handle key press in the email input
  const handleEmailKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  // Handle key press in the domain input
  const handleDomainKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddDomain();
    }
  };
  
  return (
    <div className="space-y-4">
      <Label htmlFor="accessControl">Who can access this assistant?</Label>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        {/* Private access option */}
        <div 
          className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all ${
            value === 'private' 
              ? 'border-primary bg-primary/5' 
              : 'border-border hover:border-primary/50'
          }`}
          onClick={() => handleAccessTypeChange('private')}
        >
          <div className="flex flex-col space-y-3">
            <div className="flex items-center justify-center gap-2">
              <div className={`rounded-full p-2 ${
                value === 'private' ? 'bg-primary text-primary-foreground' : 'bg-muted'
              }`}>
                <Lock className="h-4 w-4" />
              </div>
              <h3 className="font-medium">Private</h3>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground break-words">
                Only you can access
              </p>
            </div>
          </div>
          <div className="absolute top-3 right-3">
            {value === 'private' && <CheckCircle2 className="h-5 w-5 text-primary" />}
          </div>
        </div>
        
        {/* Group access option */}
        <div 
          className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all ${
            value === 'group' 
              ? 'border-primary bg-primary/5' 
              : 'border-border hover:border-primary/50'
          }`}
          onClick={() => handleAccessTypeChange('group')}
        >
          <div className="flex flex-col space-y-3">
            <div className="flex items-center justify-center gap-2">
              <div className={`rounded-full p-2 ${
                value === 'group' ? 'bg-primary text-primary-foreground' : 'bg-muted'
              }`}>
                <Users2 className="h-4 w-4" />
              </div>
              <h3 className="font-medium">Groups</h3>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground break-words">
                Predefined teams or groups
              </p>
            </div>
          </div>
          <div className="absolute top-3 right-3">
            {value === 'group' && <CheckCircle2 className="h-5 w-5 text-primary" />}
          </div>
        </div>
        
        {/* Domain access option - only show if domain is available */}
        {userDomain && (
          <div 
            className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all ${
              value === 'domain' 
                ? 'border-primary bg-primary/5' 
                : 'border-border hover:border-primary/50'
            }`}
            onClick={() => handleAccessTypeChange('domain')}
          >
            <div className="flex flex-col space-y-3">
              <div className="flex items-center justify-center gap-2">
                <div className={`rounded-full p-2 ${
                  value === 'domain' ? 'bg-primary text-primary-foreground' : 'bg-muted'
                }`}>
                  <Building2 className="h-4 w-4" />
                </div>
                <h3 className="font-medium">Organization</h3>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground break-words overflow-wrap-anywhere">
                  Only @{userDomain} users
                </p>
              </div>
            </div>
            <div className="absolute top-3 right-3">
              {value === 'domain' && <CheckCircle2 className="h-5 w-5 text-primary" />}
            </div>
          </div>
        )}
        
        {/* Multiple domains option */}
        <div 
          className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all ${
            value === 'domains' 
              ? 'border-primary bg-primary/5' 
              : 'border-border hover:border-primary/50'
          }`}
          onClick={() => handleAccessTypeChange('domains')}
        >
          <div className="flex flex-col space-y-3">
            <div className="flex items-center justify-center gap-2">
              <div className={`rounded-full p-2 ${
                value === 'domains' ? 'bg-primary text-primary-foreground' : 'bg-muted'
              }`}>
                <Building className="h-4 w-4" />
              </div>
              <h3 className="font-medium">Domains</h3>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground break-words">
                Users from specified domains
              </p>
            </div>
          </div>
          <div className="absolute top-3 right-3">
            {value === 'domains' && <CheckCircle2 className="h-5 w-5 text-primary" />}
          </div>
        </div>
        
        {/* Specific emails option */}
        <div 
          className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all ${
            value === 'specific' 
              ? 'border-primary bg-primary/5' 
              : 'border-border hover:border-primary/50'
          }`}
          onClick={() => handleAccessTypeChange('specific')}
        >
          <div className="flex flex-col space-y-3">
            <div className="flex items-center justify-center gap-2">
              <div className={`rounded-full p-2 ${
                value === 'specific' ? 'bg-primary text-primary-foreground' : 'bg-muted'
              }`}>
                <Users className="h-4 w-4" />
              </div>
              <h3 className="font-medium">Users</h3>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground break-words">
                Selected email addresses
              </p>
            </div>
          </div>
          <div className="absolute top-3 right-3">
            {value === 'specific' && <CheckCircle2 className="h-5 w-5 text-primary" />}
          </div>
        </div>
        
        {/* Public access option */}
        <div 
          className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all ${
            value === 'public' 
              ? 'border-primary bg-primary/5' 
              : 'border-border hover:border-primary/50'
          }`}
          onClick={() => handleAccessTypeChange('public')}
        >
          <div className="flex flex-col space-y-3">
            <div className="flex items-center justify-center gap-2">
              <div className={`rounded-full p-2 ${
                value === 'public' ? 'bg-primary text-primary-foreground' : 'bg-muted'
              }`}>
                <Globe className="h-4 w-4" />
              </div>
              <h3 className="font-medium">Public</h3>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground break-words">
                Anyone can access
              </p>
            </div>
          </div>
          <div className="absolute top-3 right-3">
            {value === 'public' && <CheckCircle2 className="h-5 w-5 text-primary" />}
          </div>
        </div>
      </div>
      
      {/* Show domain input when "domains" option is selected */}
      {value === 'domains' && (
        <div className="mt-4 space-y-3 bg-muted/20 p-4 rounded-lg border">
          <div className="flex items-center gap-2">
            <Building className="h-4 w-4 text-muted-foreground" />
            <Label className="font-medium">Add domains</Label>
          </div>
          
          <div className="flex gap-2">
            <Input
              placeholder="Enter domain (e.g., example.com)"
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value)}
              onKeyDown={handleDomainKeyPress}
              className="flex-1"
            />
            <Button 
              type="button" 
              onClick={handleAddDomain}
              variant="secondary"
              size="icon"
              disabled={!domainInput.trim() || !validateDomain(domainInput)}
            >
              <PlusCircle className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Display list of added domains */}
          <div className="flex flex-wrap gap-2 mt-2 max-h-40 overflow-y-auto scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent pr-1">
            {domains.length > 0 ? (
              domains.map(domain => (
                <div key={domain} className="flex items-center bg-secondary text-secondary-foreground rounded-full px-3 py-1 text-sm min-w-0 max-w-full">
                  <span className="break-words mr-1 min-w-0">@{domain}</span>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveDomain(domain);
                    }}
                    className="ml-1 flex-shrink-0 text-secondary-foreground hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">No domains added</p>
            )}
          </div>
        </div>
      )}
      
      {/* Show group selection when "group" option is selected */}
      {value === 'group' && (
        <div className="mt-4 space-y-3 bg-muted/20 p-4 rounded-lg border">
          <div className="flex items-center gap-2">
            <Users2 className="h-4 w-4 text-muted-foreground" />
            <Label className="font-medium">Select groups</Label>
          </div>
          
          {/* Display available groups as checkboxes */}
          <div className="space-y-2 mt-2 max-h-64 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent">
            {availableGroups.length > 0 ? (
              availableGroups.map(group => (
                <div 
                  key={group.id} 
                  className="flex items-start gap-3 p-3 rounded-md hover:bg-secondary/30 cursor-pointer border border-transparent hover:border-muted transition-all"
                  onClick={() => handleToggleGroup(group.id)}
                >
                  <input 
                    type="checkbox"
                    id={`group-${group.id}`}
                    checked={groups.includes(group.id)}
                    onChange={(e) => {
                      // Prevent the parent div's onClick from being triggered
                      e.stopPropagation();
                      handleToggleGroup(group.id);
                    }}
                    className="h-4 w-4 mt-0.5 rounded border-gray-300 text-primary focus:ring-primary shrink-0"
                  />
                  <div className="flex flex-col flex-1 min-w-0">
                    <label 
                      htmlFor={`group-${group.id}`}
                      className="text-sm font-medium cursor-pointer break-words"
                    >
                      {group.name}
                    </label>
                    {group.description && (
                      <span className="text-xs text-muted-foreground break-words mt-1">{group.description}</span>
                    )}
                    <span className="text-xs text-muted-foreground mt-1">{group.emails.length} member{group.emails.length !== 1 ? 's' : ''}</span>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">No groups available</p>
            )}
          </div>
          
          {/* Summary of selected groups */}
          {groups.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium mb-2">Selected groups:</h4>
              <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent pr-1">
                {groups.map(groupId => {
                  const group = availableGroups.find(g => g.id === groupId);
                  return (
                    <div key={groupId} className="flex items-center bg-secondary text-secondary-foreground rounded-full px-3 py-1 text-sm min-w-0 max-w-full">
                      <span className="break-words mr-1 min-w-0">{group?.name || groupId}</span>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleGroup(groupId);
                        }}
                        className="ml-1 flex-shrink-0 text-secondary-foreground hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Show email input when "specific" option is selected */}
      {value === 'specific' && (
        <div className="mt-4 space-y-3 bg-muted/20 p-4 rounded-lg border">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-muted-foreground" />
            <Label className="font-medium">Add specific emails</Label>
          </div>
          
          <div className="flex gap-2">
            <Input
              placeholder="Enter email address"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={handleEmailKeyPress}
              className="flex-1"
            />
            <Button 
              type="button" 
              onClick={handleAddEmail}
              variant="secondary"
              size="icon"
              disabled={!emailInput.trim() || !validateEmail(emailInput)}
            >
              <PlusCircle className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Display list of added emails */}
          <div className="flex flex-wrap gap-2 mt-2 max-h-40 overflow-y-auto scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent pr-1">
            {emails.length > 0 ? (
              emails.map(email => (
                <div key={email} className="flex items-center bg-secondary text-secondary-foreground rounded-full px-3 py-1 text-sm min-w-0 max-w-full">
                  <span className="break-words mr-1 min-w-0">{email}</span>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveEmail(email);
                    }}
                    className="ml-1 flex-shrink-0 text-secondary-foreground hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">No email addresses added</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}